/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */

import type {
  DOMConversionMap,
  DOMConversionOutput,
  DOMExportOutput,
  EditorConfig,
  LexicalEditor,
  LexicalNode,
  NodeKey,
  SerializedLexicalNode,
  Spread,
} from 'lexical';
import { DecoratorNode } from 'lexical';
import * as React from 'react';

type Dimension = number | 'inherit';

const ExcalidrawComponent = React.lazy(() => import('./ExcalidrawComponent'));

export type SerializedExcalidrawNode = Spread<
  {
    data: string;
    width?: Dimension;
    height?: Dimension;
  },
  SerializedLexicalNode
>;

function $convertExcalidrawElement(
  domNode: HTMLElement
): DOMConversionOutput | null {
  const excalidrawData = domNode.getAttribute('data-lexical-excalidraw-json');
  const widthStr = domNode?.style.width;
  const heightStr = domNode?.style.height;

  const height =
    !heightStr || heightStr === 'inherit' ? 'inherit' : parseInt(heightStr, 10);
  const width =
    !widthStr || widthStr === 'inherit' ? 'inherit' : parseInt(widthStr, 10);

  if (excalidrawData) {
    const node = $createExcalidrawNode(excalidrawData, width, height);
    return {
      node,
    };
  }
  return null;
}

export class ExcalidrawNode extends DecoratorNode<JSX.Element> {
  __data: string;
  __width: Dimension;
  __height: Dimension;
  __svg: string;

  static override getType(): string {
    return 'excalidraw';
  }

  static override clone(node: ExcalidrawNode): ExcalidrawNode {
    const cloned = new ExcalidrawNode(
      node.__data,
      node.__width,
      node.__height,
      node.__key
    );
    cloned.__svg = node.__svg;
    return cloned;
  }

  static override importJSON(
    serializedNode: SerializedExcalidrawNode
  ): ExcalidrawNode {
    return new ExcalidrawNode(
      serializedNode.data,
      serializedNode.width ?? 'inherit',
      serializedNode.height ?? 'inherit'
    ).updateFromJSON(serializedNode);
  }

  override exportJSON(): SerializedExcalidrawNode {
    return {
      ...super.exportJSON(),
      data: this.__data,
      height: this.__height === 'inherit' ? undefined : this.__height,
      width: this.__width === 'inherit' ? undefined : this.__width,
    };
  }

  constructor(
    data = '[]',
    width: Dimension = 'inherit',
    height: Dimension = 'inherit',
    key?: NodeKey
  ) {
    super(key);
    this.__data = data;
    this.__width = width;
    this.__height = height;
    this.__svg = '';
  }

  // View
  override createDOM(config: EditorConfig): HTMLElement {
    const span = document.createElement('span');
    const theme = config.theme;
    const className = theme.image;
    if (className !== undefined) {
      span.className = className;
    }
    return span;
  }

  override updateDOM(): false {
    return false;
  }

  static override importDOM(): DOMConversionMap<HTMLSpanElement> | null {
    return {
      span: (domNode: HTMLSpanElement) => {
        if (!domNode.hasAttribute('data-lexical-excalidraw-json')) {
          return null;
        }
        return {
          conversion: $convertExcalidrawElement,
          priority: 1,
        };
      },
    };
  }

  override exportDOM(editor: LexicalEditor): DOMExportOutput {
    const element = document.createElement('span');

    element.style.display = 'inline-block';

    let svgFound = false;

    // Primary: Use stored SVG if available
    if (this.__svg && this.__svg.trim() !== '') {
      element.innerHTML = this.__svg;
      svgFound = true;
    }

    // Fallback 1: Try to find SVG in DOM
    if (!svgFound) {
      const content = editor.getElementByKey(this.getKey());

      if (content !== null) {
        const svg =
          content.querySelector('svg') ||
          content.getElementsByTagName('svg')[0];

        if (svg) {
          element.innerHTML = svg.outerHTML;
          svgFound = true;
        }

        // Fallback 2: Check for data-svg attribute
        if (!svgFound) {
          const imgContainer = content.querySelector('[data-svg]');

          if (imgContainer) {
            const svgData = imgContainer.getAttribute('data-svg');
            if (svgData) {
              element.innerHTML = svgData;
              svgFound = true;
            }
          }
        }
      }
    }

    element.style.width =
      this.__width === 'inherit' ? 'inherit' : `${this.__width}px`;
    element.style.height =
      this.__height === 'inherit' ? 'inherit' : `${this.__height}px`;

    element.setAttribute('data-lexical-excalidraw-json', this.__data);
    return { element };
  }

  setData(data: string, width?: Dimension, height?: Dimension): void {
    const self = this.getWritable();
    self.__data = data;
    if (width !== undefined) {
      self.__width = width;
    }
    if (height !== undefined) {
      self.__height = height;
    }
  }

  setSvg(svg: string): void {
    const self = this.getWritable();
    self.__svg = svg;
  }

  getSvg(): string {
    return this.getLatest().__svg;
  }

  getWidth(): Dimension {
    return this.getLatest().__width;
  }

  setWidth(width: Dimension): void {
    const self = this.getWritable();
    self.__width = width;
  }

  getHeight(): Dimension {
    return this.getLatest().__height;
  }

  setHeight(height: Dimension): void {
    const self = this.getWritable();
    self.__height = height;
  }

  override decorate(editor: LexicalEditor, config: EditorConfig): JSX.Element {
    return (
      <ExcalidrawComponent
        nodeKey={this.getKey()}
        data={this.__data}
        width={this.__width}
        height={this.__height}
      />
    );
  }
}

export function $createExcalidrawNode(
  data = '[]',
  width: Dimension = 'inherit',
  height: Dimension = 'inherit'
): ExcalidrawNode {
  return new ExcalidrawNode(data, width, height);
}

export function $isExcalidrawNode(
  node: LexicalNode | null | undefined
): node is ExcalidrawNode {
  return node instanceof ExcalidrawNode;
}
